/**
 * Drops add-on line items (Tempdrop Care, Care+) as soon as the cart no longer holds the main
 * product. Removal is silent: the add-on goes away together with the product it belonged to.
 *
 * Two entry points are needed because the theme handles the two surfaces differently. The cart
 * drawer updates over ajax and announces `cart:change`, while the /cart page reloads itself after
 * every line change, so that case is caught on the next page load instead.
 */
(() => {
  const config = window.tdCartAddonGuard;

  const hasIds = (value) => Array.isArray(value) && value.length > 0;

  if (!hasIds(config?.mainProductIds) || !hasIds(config?.addonProductIds)) {
    return;
  }

  const mainProductIds = new Set(config.mainProductIds);
  const addonProductIds = new Set(config.addonProductIds);
  let isRemoving = false;

  const isCartPage = () => window.themeVariables?.settings?.pageType === 'cart';

  const findOrphanedAddons = (items) => {
    if (!Array.isArray(items) || items.length === 0) return [];

    const hasMainProduct = items.some((item) => mainProductIds.has(item.product_id) && item.quantity > 0);
    if (hasMainProduct) return [];

    return items.filter((item) => addonProductIds.has(item.product_id) && item.quantity > 0);
  };

  /**
   * One request for every orphan. Keying `updates` by variant id zeroes each matching line
   * wherever it sits in the cart, including a line carrying a selling plan.
   */
  const removeLines = async (orphans) => {
    const updates = {};
    orphans.forEach((item) => {
      updates[item.variant_id] = 0;
    });

    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      throw new Error(response.statusText);
    }
  };

  const enforce = async (items) => {
    if (isRemoving) return;

    const orphans = findOrphanedAddons(items);
    if (orphans.length === 0) return;

    isRemoving = true;

    try {
      await removeLines(orphans);
    } catch {
      // Leaving the add-on in place is better than a half-updated cart; checkout still shows it.
      return;
    } finally {
      isRemoving = false;
    }

    if (isCartPage()) {
      window.location.reload();
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
  };

  document.addEventListener('cart:change', (event) => {
    enforce(event.detail?.cart?.items);
  });

  enforce(config.items);
})();
